<template>
  <v-navigation-drawer
    :value="sidenavOpen"
    :clipped="$vuetify.breakpoint.lgAndUp"
    fixed
    app
    @input="UPDATE_SETTING({ key: 'sidenavOpen', value: $event })"
  >
    <div v-if="$vuetify.breakpoint.mdAndDown" class="ma-3 text-center">
      <img
        src="../assets/LTOU_logo.png"
        alt="logo"
        class="clickable"
        height="30"
        @click="$router.push({ path: '/' }).catch(() => {})"
      />
    </div>
    <v-list dense nav>
      <template v-for="(item, index) in items">
        <v-divider v-if="item.divider" :key="`divider-${index}`" class="mb-1" />

        <v-list-item
          v-else-if="!item.children && hasAccess(item.requiredPermissions, item.allowedRoles)"
          :prepend-icon="item.icon"
          :key="item.key"
          :to="item.route"
          :disabled="!item.route"
          active-class="primary--text"
          link
        >
          <v-list-item-icon>
            <v-badge
              v-if="item.hasBadge && item.badgeContent > 0"
              :content="item.badgeContent"
              overlap
              bottom
              color="red"
            >
              <v-icon>{{ item.icon }}</v-icon>
            </v-badge>
            <v-icon v-else>
              {{ item.icon }}
            </v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            {{ $t(item.text) }}
          </v-list-item-title>
        </v-list-item>

        <v-list-group
          v-else-if="hasAccess(item.requiredPermissions, item.allowedRoles)"
          :value="!!expandedGroupsMap[item.menuGroup]"
          :key="item.key"
          :prepend-icon="item.icon"
          @input="toggleGroupExpand(item.menuGroup, $event)"
        >
          <template v-slot:activator>
            <v-list-item-title style="white-space: normal">
              {{ $t(item.text) }}
            </v-list-item-title>
          </template>

          <template v-for="child in item.children">
            <v-list-item
              v-if="hasAccess(child.requiredPermissions, child.allowedRoles)"
              :key="child.key"
              :to="child.route"
              :disabled="!child.route"
              class="pl-2"
              link
            >
              <v-list-item-icon>
                <!-- <v-icon>-->
                <!--  {{ child.icon }}-->
                <!-- </v-icon>-->
              </v-list-item-icon>
              <v-list-item-title style="white-space: normal">
                {{ $t(child.text) }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-list-group>
      </template>

      <v-list-item link @click="onLogout">
        <v-list-item-icon>
          <v-icon>mdi-logout</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('logout') }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
/* eslint-disable indent */

import { mapActions, mapMutations, mapState } from 'vuex';
import authService from '@/api/auth-service';

export default {
  name: 'NavigationDrawer',

  data() {
    return {
      expandedGroupsMap: {},
      drivingPermitsCountIntervalId: null,
    };
  },

  computed: {
    ...mapState('settings', ['sidenavOpen']),
    ...mapState('permits', ['permitFilterParams']),
    ...mapState('subjects', ['subjectsFilterParams']),
    ...mapState('users', ['userFilterParams']),
    ...mapState('documents', ['documentFilterParams']),
    ...mapState('workPlaces', ['workPlaceFilterParams']),
    ...mapState('livingAddresses', ['livingAddressFilterParams']),
    ...mapState('permitStatistics', ['permitStatisticsFilterParams']),
    ...mapState('loginHistory', ['loginHistoryFilterParams']),
    ...mapState('changeHistory', ['changeHistoryFilterParams']),
    ...mapState('trainingResults', ['trainingResultFilterParams']),
    ...mapState('permitStatistics', ['permitStatusStatisticsFilterParams']),
    ...mapState('emailLogs', ['emailLogsFilterParams']),
    ...mapState('permitIssues', ['permitIssuesFilterParams']),
    ...mapState('issuedVehiclePermits', ['issuedVehiclePermitsFilterParams']),
    ...mapState('unauthorizedAttempts', ['unauthorizedAttemptsFilterParams']),
    ...mapState('backgroundChecks', ['backgroundChecksFilterParams']),
    ...mapState('impeccableReputationChecks', ['impeccableReputationChecksFilterParams']),
    ...mapState('checkInHistory', ['checkInHistoryFilterParams']),
    ...mapState('printList', ['printListFilterParams']),
    ...mapState('drivingPermits', ['drivingPermitFilterParams', 'permitsWithPassedExamsCount']),
    ...mapState('drivingPracticeExams', ['drivingPracticeExamFilterParams']),
    ...mapState('drivingPermitStatistics', ['drivingPermitStatisticsFilterParams']),
    ...mapState('rejectedPermits', ['rejectedPermitFilterParams']),

    items() {
      let items = [
        {
          key: 'home',
          icon: 'mdi-home',
          text: 'home_page',
          route: { name: 'home', query: this.permitStatusStatisticsFilterParams },
          allowedRoles: ['admin', 'ltou_admin', 'ltou_employee', 'subject_admin'],
        },
        {
          key: 'permits',
          icon: 'mdi-file-document-edit',
          text: 'permits',
          route: { name: 'permits', query: this.permitFilterParams },
        },
        {
          key: 'driving-permits',
          icon: 'mdi-car',
          text: 'driving_permits',
          route: { name: 'drivingPermits', query: this.drivingPermitFilterParams },
          allowedRoles: ['admin', 'subject_admin', 'user'],
          requiredPermissions: ['approve_subject_driving_permit', 'driving_training_instructor'],
          hasBadge: true,
          badgeContent: this.permitsWithPassedExamsCount,
        },
        {
          key: 'parking-info',
          icon: 'mdi-parking',
          text: 'approve_parking_info_menu',
          route: { name: 'approveParkingInfo' },
          allowedRoles: ['admin', 'ltou_admin'],
          requiredPermissions: ['approve_parking_info'],
        },
        {
          key: 'subjects',
          icon: 'mdi-domain',
          text: 'subjects',
          route: { name: 'subjects', query: this.subjectsFilterParams },
          allowedRoles: ['admin', 'ltou_admin', 'ltou_employee'],
          requiredPermissions: ['create_subject'],
        },
        {
          key: 'documents',
          icon: 'mdi-file-document-multiple',
          text: 'documents',
          route: { name: 'documents', query: this.documentFilterParams },
        },
        {
          key: 'statistics',
          icon: 'mdi-chart-box',
          text: 'statistics',
          allowedRoles: ['admin', 'subject_admin', 'ltou_admin', 'ltou_employee'],
          requiredPermissions: ['driving_training_instructor'],
          menuGroup: 'statistics',
          children: [
            {
              key: 'permit_statistics',
              icon: 'mdi-chart-box',
              text: 'permit_statistic',
              route: { name: 'permitStatistics', query: this.permitStatisticsFilterParams },
              allowedRoles: ['admin', 'subject_admin', 'ltou_admin', 'ltou_employee'],
            },
            {
              key: 'driving_permit_statistics',
              icon: 'mdi-chart-box',
              text: 'driving_permit_statistic',
              route: {
                name: 'drivingPermitStatistics',
                query: this.drivingPermitStatisticsFilterParams,
              },
              requiredPermissions: ['driving_training_instructor'],
            },
            {
              key: 'issued_vehicle_permits',
              icon: 'mdi-car',
              text: 'issued_vehicle_permits_menu',
              route: { name: 'issuedVehiclePermits', query: this.issuedVehiclePermitsFilterParams },
              allowedRoles: ['admin', 'ltou_admin', 'ltou_employee'],
            },
          ],
        },
        {
          key: 'work-places',
          icon: 'mdi-briefcase',
          text: 'work_places_menu',
          route: { name: 'workPlaces', query: this.workPlaceFilterParams },
        },
        {
          key: 'living-addresses',
          icon: 'mdi-home-map-marker',
          text: 'living_addresses_menu',
          route: { name: 'livingAddresses', query: this.livingAddressFilterParams },
        },
        {
          key: 'driving-practice-exams',
          icon: 'mdi-school',
          text: 'practice_trainings',
          route: {
            name: 'drivingPracticeExams',
            query: this.drivingPracticeExamFilterParams,
          },
          allowedRoles: ['admin'],
          requiredPermissions: ['driving_training_instructor'],
        },
        {
          key: 'training-results',
          icon: 'mdi-clipboard-check',
          text: 'training_results',
          route: { name: 'trainingResults', query: this.trainingResultFilterParams },
        },
        {
          key: 'background-checks',
          icon: 'mdi-clipboard-flow',
          text: 'data_exchange',
          route: { name: 'backgroundChecks', query: this.backgroundChecksFilterParams },
          allowedRoles: ['admin', 'ltou_admin', 'ltou_employee'],
        },
        {
          key: 'impeccable-reputation-checks',
          icon: 'mdi-clipboard-account',
          text: 'background_checks',
          route: {
            name: 'impeccableReputationChecks',
            query: this.impeccableReputationChecksFilterParams,
          },
          allowedRoles: ['admin'],
        },
        {
          key: 'rejected-permits',
          icon: 'mdi-file-document-remove',
          text: 'rejected_permits',
          route: {
            name: 'rejectedPermits',
            query: this.rejectedPermitFilterParams,
          },
          allowedRoles: ['admin'],
        },
        {
          key: 'location-check-in',
          icon: 'mdi-barcode-scan',
          text: 'location_check_in',
          route: { name: 'locationCheckInHistory', query: this.checkInHistoryFilterParams },
          allowedRoles: ['admin', 'ltou_admin', 'ltou_employee'],
          requiredPermissions: ['view_locations'],
        },
        {
          key: 'print-logs',
          icon: 'mdi-printer',
          text: 'print_logs',
          route: { name: 'printList', query: this.printListFilterParams },
          requiredPermissions: ['show_print_log'],
          allowedRoles: ['admin', 'ltou_admin'],
        },
        {
          key: 'permit-issues',
          icon: 'mdi-alert-circle-outline',
          text: 'permit_issues_menu',
          allowedRoles: ['admin', 'ltou_admin'],
          requiredPermissions: ['is_visible_access_attempts'],
          menuGroup: 'permit-issues',
          children: [
            {
              icon: 'mdi-bell-ring',
              text: 'notifications',
              route: { name: 'permitIssues', query: this.permitIssuesFilterParams },
            },
            {
              icon: 'mdi-history',
              text: 'history',
              route: { name: 'unauthorizedAttempts', query: this.unauthorizedAttemptsFilterParams },
            },
          ],
        },
        {
          key: 'users',
          icon: 'mdi-account-multiple',
          text: 'users',
          route: { name: 'users', query: this.userFilterParams },
          allowedRoles: ['admin', 'subject_admin', 'ltou_admin', 'ltou_employee'],
          requiredPermissions: ['see_subject_admins_who_is_allowed_to_approve_driving_permit']
        },
        {
          key: 'settings',
          icon: 'mdi-cog',
          text: 'settings',
          allowedRoles: ['admin', 'ltou_admin', 'ltou_employee'],
          requiredPermissions: ['driving_training_instructor'],
          menuGroup: 'settings',
          children: [
            {
              icon: 'mdi-history',
              text: 'change_history',
              route: { name: 'changeHistory', query: this.changeHistoryFilterParams },
              allowedRoles: ['admin', 'ltou_admin'],
            },
            {
              icon: 'mdi-clipboard-pulse',
              text: 'system_log_files',
              route: { name: 'systemLogFiles' },
              allowedRoles: ['admin'],
            },
            {
              icon: 'mdi-account-key',
              text: 'login_history',
              route: { name: 'loginHistory', query: this.loginHistoryFilterParams },
              allowedRoles: ['admin'],
            },
            {
              icon: 'mdi-email-newsletter',
              text: 'email_logs_menu',
              route: { name: 'emailLogs', query: this.emailLogsFilterParams },
              allowedRoles: ['admin', 'ltou_admin', 'ltou_employee'],
            },
            {
              icon: 'mdi-playlist-edit',
              text: 'permit_fields',
              route: { name: 'permitFields' },
              allowedRoles: ['admin'],
            },
            {
              icon: 'mdi-currency-eur',
              text: 'permit_prices',
              route: { name: 'permitPrices' },
              allowedRoles: ['admin', 'ltou_admin'],
              requiredPermissions: ['driving_training_instructor'],
            },
            {
              icon: 'mdi-airplane',
              text: 'airports',
              route: { name: 'airports' },
              allowedRoles: ['admin'],
            },
            {
              icon: 'mdi-cog',
              text: 'system_settings',
              route: { name: 'systemSettings' },
              allowedRoles: ['admin', 'ltou_admin'],
              requiredPermissions: ['driving_training_instructor'],
            },
            {
              icon: 'mdi-earth',
              text: 'countries',
              route: { name: 'countries' },
              allowedRoles: ['admin'],
            },
            {
              icon: 'mdi-desktop-classic',
              text: 'this_device',
              route: { name: 'thisDevice' },
              allowedRoles: ['admin', 'ltou_admin'],
            },
            {
              icon: '',
              text: 'driving_permit_questionnaire',
              route: { name: 'questionnaires' },
              allowedRoles: ['admin'],
              requiredPermissions: ['driving_training_instructor'],
            },
            {
              icon: 'mdi-crosshairs-gps',
              text: 'zones',
              route: { name: 'zones' },
              allowedRoles: ['admin'],
            },
            {
              icon: 'mdi-cog',
              text: 'exams',
              route: { name: 'exams' },
              allowedRoles: ['admin', 'ltou_admin'],
            }
          ],
        },
        { divider: true },
        {
          icon: 'mdi-account',
          text: 'my_profile',
          route: 'profile',
        },
      ];

      if (this.currentUser.role === 'impeccable_reputation_institution_employee') {
        items = [
          {
            key: 'background-checks',
            icon: 'mdi-clipboard-account',
            text: 'background_checks',
            route: {
              name: 'impeccableReputationChecks',
              query: this.impeccableReputationChecksFilterParams,
            },
            allowedRoles: ['admin', 'impeccable_reputation_institution_employee'],
            requiredPermissions: [],
          },
          {
            key: 'rejected-permits',
            icon: 'mdi-file-document-remove',
            text: 'rejected_permits',
            route: {
              name: 'rejectedPermits',
              query: this.rejectedPermitFilterParams,
            },
            allowedRoles: ['admin', 'impeccable_reputation_institution_employee'],
            requiredPermissions: [],
          },
          { divider: true },
          { icon: 'mdi-account', text: 'my_profile', route: 'profile' },
        ];
      }

      if (this.$store.state.settings.isPrintTerminal) {
        items = [
          {
            key: 'permits',
            icon: 'mdi-file-document-edit',
            text: 'permits',
            route: { name: 'permits', query: this.permitFilterParams },
            requiredPermissions: [],
            allowedRoles: [],
          },
          {
            key: 'settings',
            icon: 'mdi-cog',
            text: 'settings',
            allowedRoles: ['admin', 'ltou_admin', 'ltou_employee'],
            requiredPermissions: [],
            menuGroup: 'settings',
            children: [
              {
                icon: 'mdi-desktop-classic',
                text: 'this_device',
                route: { name: 'thisDevice' },
                allowedRoles: ['admin', 'ltou_admin'],
                requiredPermissions: [],
              },
            ],
          },
          { divider: true },
          { icon: 'mdi-account', text: 'my_profile', route: 'profile' },
        ];
      }

      items = items.filter((item) => {
        // Driving permits are shown starting year 2024
        // TODO this should be removed in year 2024
        if (item.key === 'driving-permits') {
          const currentYear = new Date().getFullYear();
          return currentYear > 2023;
        }
        return item;
      });

      return items;
    },

    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
  },

  watch: {
    $route: {
      handler(newValue) {
        if (!newValue.meta.menuGroup) {
          return;
        }
        this.toggleGroupExpand(newValue.meta.menuGroup, true);
      },
      immediate: true,
    },
  },

  created() {
    if (this.$hasPermission(['driving_training_instructor']) && !this.$isAdmin()) {
      // fetch count only for training instructor and no one else
      this.setupDrivingPermitsCountFetching();
    }
  },

  beforeDestroy() {
    clearInterval(this.drivingPermitsCountIntervalId);
  },

  methods: {
    ...mapMutations('settings', ['UPDATE_SETTING']),
    ...mapActions('auth', ['logout']),
    ...mapActions('drivingPermits', ['getPermitsWithPassedExamsCount']),

    setupDrivingPermitsCountFetching() {
      this.getPermitsWithPassedExamsCount();

      this.drivingPermitsCountIntervalId = setInterval(() => {
        this.getPermitsWithPassedExamsCount();
      }, 30000);
    },

    hasAccess(requiredPermissions, allowedRoles) {
      if (!requiredPermissions?.length && !allowedRoles?.length) {
        return true;
      }
      if (requiredPermissions?.length && this.$hasPermission(requiredPermissions)) {
        return true;
      }
      if (allowedRoles?.length && this.$hasCorrectRole(allowedRoles)) {
        return true;
      }
      return false;
    },

    toggleGroupExpand(key, value) {
      if (!key) {
        return;
      }

      this.$set(this.expandedGroupsMap, key, value);
    },

    onLogout() {
      return authService.logout().finally(() => {
        this.logout();
      });
    },
  },
};
</script>
